import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rsvp() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    attending: "yes",
    guests: "1",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || form.full_name.trim().length < 2) {
      toast.error("Veuillez indiquer votre nom complet.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        attending: form.attending,
        guests: Number(form.guests) || 1,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/rsvp`, payload);
      setDone(true);
      toast.success("Merci, votre réponse est enregistrée. Barakallahu fikum.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Une erreur est survenue. Réessayez.";
      toast.error(typeof detail === "string" ? detail : "Erreur de soumission.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="rsvp-section"
      className="relative section-pad"
    >

      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="gold-rule mb-6">RSVP</p>
          <h2 className="font-serif-display text-emerald-deep text-4xl md:text-5xl">
            Confirmez votre présence
          </h2>
          <p className="font-serif-display italic text-emerald/80 mt-3 max-w-xl mx-auto">
            Votre présence rendra ce jour inoubliable. Merci de répondre
            avant le 30 juillet 2026.
          </p>
        </div>

        {done ? (
          <div
            data-testid="rsvp-success"
            className="ornate-frame bg-cream p-12 text-center"
          >
            <p className="font-script gold-text text-6xl">Merci !</p>
            <p className="font-serif-display italic text-emerald-deep text-xl mt-4">
              Votre réponse a bien été enregistrée. Qu’Allah vous bénisse.
            </p>
            <button
              type="button"
              data-testid="rsvp-reset-btn"
              onClick={() => {
                setDone(false);
                setForm({
                  full_name: "",
                  email: "",
                  phone: "",
                  attending: "yes",
                  guests: "1",
                  message: "",
                });
              }}
              className="btn-outline-gold mt-8"
            >
              Envoyer une autre réponse
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="rsvp-form"
            className="ornate-frame bg-cream p-8 md:p-12 grid gap-6"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                  NOM COMPLET *
                </Label>
                <Input
                  data-testid="rsvp-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className="mt-2 bg-white/80 border-[#C9A961]/50 focus-visible:ring-[#C9A961]"
                  placeholder="Votre nom et prénom"
                  required
                />
              </div>
              <div>
                <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                  TÉLÉPHONE
                </Label>
                <Input
                  data-testid="rsvp-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-2 bg-white/80 border-[#C9A961]/50 focus-visible:ring-[#C9A961]"
                  placeholder="+241 ..."
                />
              </div>
            </div>

            <div>
              <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                EMAIL
              </Label>
              <Input
                data-testid="rsvp-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="mt-2 bg-white/80 border-[#C9A961]/50 focus-visible:ring-[#C9A961]"
                placeholder="vous@exemple.com"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                  SEREZ-VOUS PRÉSENT(E) ?
                </Label>
                <RadioGroup
                  value={form.attending}
                  onValueChange={(v) => update("attending", v)}
                  className="mt-3 flex gap-6"
                  data-testid="rsvp-attending"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="yes" id="att-yes" />
                    <span className="font-serif-display text-lg text-emerald-deep">Avec joie</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="no" id="att-no" />
                    <span className="font-serif-display text-lg text-emerald-deep">Avec regret</span>
                  </label>
                </RadioGroup>
              </div>

              <div>
                <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                  NOMBRE D’INVITÉS
                </Label>
                <Select
                  value={form.guests}
                  onValueChange={(v) => update("guests", v)}
                >
                  <SelectTrigger
                    data-testid="rsvp-guests"
                    className="mt-2 bg-white/80 border-[#C9A961]/50 focus:ring-[#C9A961]"
                  >
                    <SelectValue placeholder="1" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n} {n > 1 ? "personnes" : "personne"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-emerald-deep tracking-[0.25em] text-xs">
                MESSAGE POUR LES MARIÉS
              </Label>
              <Textarea
                data-testid="rsvp-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="mt-2 bg-white/80 border-[#C9A961]/50 focus-visible:ring-[#C9A961] min-h-[120px]"
                placeholder="Vos vœux, prières, ou un mot du cœur..."
                maxLength={600}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-2">
              <p className="text-emerald/70 italic text-sm">
                Réponse avant le 30 juillet 2026.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="rsvp-submit-btn"
                className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Envoi..." : "Envoyer ma réponse"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
